import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Download, Trash2, Mail, Phone, MapPin, Search, Globe, BadgeCheck, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { LEVEL_COLORS } from '../bimmap/bimmapConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBimmapLeads = () => {
  const [leads, setLeads] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const [lr, wr] = await Promise.all([
        axios.get(`${API}/admin/bimmap/leads`),
        axios.get(`${API}/admin/bimmap/waitlist`),
      ]);
      setLeads(lr.data);
      setWaitlist(wr.data);
    } catch {
      toast.error('Erro ao carregar dados do BIMMap.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []);

  const removeWaitlist = async (id) => {
    if (!window.confirm('Remover este registro da lista de espera?')) return;
    try {
      await axios.delete(`${API}/admin/bimmap/waitlist/${id}`);
      setWaitlist((l) => l.filter((x) => x.id !== id));
      toast.success('Registro removido.');
    } catch {
      toast.error('Erro ao remover.');
    }
  };

  const exportWaitlistCsv = () => {
    const cols = ['name', 'email', 'phone', 'company', 'note', 'created_at'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [cols.join(','), ...waitlist.map((l) => cols.map((c) => esc(l[c])).join(','))];
    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bimmap_waitlist_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleVerify = async (id, value) => {
    try {
      await axios.put(`${API}/admin/bimmap/profiles/${id}/verify`, { verified: value });
      setLeads((l) => l.map((x) => (x.id === id ? { ...x, verified: value } : x)));
      toast.success(value ? 'Perfil verificado ✓' : 'Selo removido.');
    } catch {
      toast.error('Erro ao atualizar verificação.');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover este perfil do mapa? Esta ação não pode ser desfeita.')) return;
    try {
      await axios.delete(`${API}/admin/bimmap/profiles/${id}`);
      setLeads((l) => l.filter((x) => x.id !== id));
      toast.success('Perfil removido.');
    } catch {
      toast.error('Erro ao remover.');
    }
  };

  const exportCsv = () => {
    const cols = ['name', 'company', 'entity_type', 'actuation', 'bim_level', 'email', 'phone', 'city', 'country', 'specializations', 'software', 'website', 'instagram', 'linkedin', 'headline', 'verified', 'created_at'];
    const esc = (v) => `"${String(Array.isArray(v) ? v.join('; ') : (v ?? '')).replace(/"/g, '""')}"`;
    const rows = [cols.join(','), ...leads.map((l) => cols.map((c) => esc(l[c])).join(','))];
    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bimmap_leads_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = leads.filter((l) => {
    if (!q) return true;
    const hay = `${l.name} ${l.company} ${l.email} ${l.phone} ${l.city} ${l.country}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-red-500" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="admin-bimmap-leads">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <Globe className="text-red-500" size={22} />
          <h3 className="text-lg font-semibold text-white">Leads BIMMap</h3>
          <Badge className="bg-zinc-700">{leads.length}</Badge>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar lead..." className="bg-zinc-800 border-zinc-600 text-white h-9 pl-9" data-testid="bimmap-leads-search" />
        </div>
        <Button onClick={exportCsv} className="bg-green-600 hover:bg-green-700 text-white h-9" data-testid="bimmap-export-csv">
          <Download size={16} className="mr-2" /> Exportar CSV
        </Button>
      </div>

      {filtered.length === 0 && <p className="text-gray-500 text-sm text-center py-6">Nenhum lead ainda.</p>}

      <div className="space-y-2">
        {filtered.map((l) => (
          <Card key={l.id} className="bg-zinc-900 border-zinc-700">
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="lg:w-1/4">
                  <div className="text-white font-medium text-sm">{l.name}</div>
                  {l.company && <div className="text-xs text-gray-400">{l.company}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className="text-[10px] bg-zinc-700">{l.entity_type === 'company' ? 'Empresa' : 'Pessoa'}</Badge>
                    <Badge className={`text-[10px] ${LEVEL_COLORS[l.bim_level] || 'bg-zinc-700'}`}>{l.bim_level}</Badge>
                    {l.verified && <Badge className="text-[10px] bg-sky-600 text-white flex items-center gap-0.5"><BadgeCheck size={11} /> Verificado</Badge>}
                  </div>
                </div>
                <div className="flex-1 grid sm:grid-cols-2 gap-1 text-xs text-gray-300">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail size={12} className="text-red-400" /> {l.email}</a>
                  <a href={`https://wa.me/${(l.phone || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white"><Phone size={12} className="text-green-400" /> {l.phone}</a>
                  <div className="flex items-center gap-1.5"><MapPin size={12} className="text-gray-500" /> {l.city}{l.country ? `, ${l.country}` : ''}</div>
                  <div className="text-gray-500">{l.actuation}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleVerify(l.id, !l.verified)}
                    className={l.verified ? 'text-sky-400 hover:bg-sky-500/10' : 'text-gray-400 hover:text-sky-400 hover:bg-sky-500/10'}
                    data-testid={`bimmap-lead-verify-${l.id}`}
                    title={l.verified ? 'Remover selo de verificado' : 'Marcar como verificado'}
                  >
                    <BadgeCheck size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(l.id)} className="text-red-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`bimmap-lead-delete-${l.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lista de espera Premium */}
      <div className="border-t border-zinc-700 pt-6 mt-8" data-testid="bimmap-waitlist-section">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Crown className="text-red-400" size={20} />
            <h3 className="text-lg font-semibold text-white">Lista de espera Premium</h3>
            <Badge className="bg-red-600">{waitlist.length}</Badge>
          </div>
          <Button onClick={exportWaitlistCsv} disabled={waitlist.length === 0} className="bg-green-600 hover:bg-green-700 text-white h-9 sm:ml-auto" data-testid="bimmap-waitlist-export-csv">
            <Download size={16} className="mr-2" /> Exportar CSV
          </Button>
        </div>
        {waitlist.length === 0 && <p className="text-gray-500 text-sm text-center py-4">Ninguém na lista de espera ainda.</p>}
        <div className="space-y-2">
          {waitlist.map((w) => (
            <Card key={w.id} className="bg-zinc-900 border-zinc-700">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="lg:w-1/4">
                    <div className="text-white font-medium text-sm">{w.name}</div>
                    {w.company && <div className="text-xs text-gray-400">{w.company}</div>}
                  </div>
                  <div className="flex-1 grid sm:grid-cols-2 gap-1 text-xs text-gray-300">
                    <a href={`mailto:${w.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail size={12} className="text-red-400" /> {w.email}</a>
                    <a href={`https://wa.me/${(w.phone || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white"><Phone size={12} className="text-green-400" /> {w.phone}</a>
                    {w.note && <div className="text-gray-500 sm:col-span-2 italic">"{w.note}"</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeWaitlist(w.id)} className="text-red-500 hover:text-red-400 hover:bg-red-500/10 shrink-0" data-testid={`bimmap-waitlist-delete-${w.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminBimmapLeads;
